// ═══════════════════════════════════════════════════════════════════════════════
// Stock take batch upload: thumbnail, enlarge (like receipt), remove control
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, type KeyboardEvent } from 'react';
import {
  useAuthedApiImageBlobUrl,
  usePairingScopedImageBlobUrl,
} from '../receipt/useReceiptImageDisplay';
import { FullscreenPinchImageViewer } from '../ui/FullscreenPinchImageViewer';

const EXPIRED_IMAGE_FALLBACK_SRC = '/img/img_expired-1.webp';

type StocktakeUploadedPhotoThumbProps = {
  displayUrl: string;
  /** When set, load via task-instance inventory stream API (persisted submission photos). */
  authedApiPath?: string;
  /** Alt and ARIA */
  imageAlt: string;
  enlargeAriaLabel: string;
  closeViewerLabel: string;
  removeAriaLabel: string;
  onRequestRemove: () => void;
  /** When true, hide action buttons (e.g. read-only) */
  actionsDisabled?: boolean;
  /** Outer frame size; default matches Step A upload list (80×80). */
  thumbClassName?: string;
};

export const StocktakeUploadedPhotoThumb = ({
  displayUrl,
  authedApiPath,
  imageAlt,
  enlargeAriaLabel,
  closeViewerLabel,
  removeAriaLabel,
  onRequestRemove,
  actionsDisabled = false,
  thumbClassName = 'h-20 w-20 flex-shrink-0',
}: StocktakeUploadedPhotoThumbProps) => {
  const authed = useAuthedApiImageBlobUrl(authedApiPath);
  const scoped = usePairingScopedImageBlobUrl(authedApiPath ? undefined : displayUrl);
  const loading = authedApiPath ? authed.loading : scoped.loading;
  const displaySrc = authedApiPath ? authed.objectUrl : scoped.displaySrc;
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleOpenViewer = () => {
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  const handleThumbKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenViewer();
    }
  };

  const handleRemoveKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!actionsDisabled) onRequestRemove();
    }
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-800 ${thumbClassName}`}
        role="status"
        aria-live="polite"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  const resolvedDisplaySrc =
    displaySrc && displaySrc.trim() !== '' ? displaySrc : EXPIRED_IMAGE_FALLBACK_SRC;

  return (
    <>
      <div
        className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-800 ${thumbClassName}`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenViewer();
          }}
          onKeyDown={handleThumbKeyDown}
          className="relative block h-full w-full overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          aria-label={enlargeAriaLabel}
          tabIndex={0}
        >
          <img src={resolvedDisplaySrc} alt={imageAlt} className="h-full w-full object-cover" />
          <span
            className="pointer-events-none absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/65 text-white shadow-sm"
            aria-hidden
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20L16.65 16.65" strokeLinecap="round" />
            </svg>
          </span>
        </button>
        {actionsDisabled ? null : (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 bg-black/50 py-0.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRequestRemove();
              }}
              onKeyDown={handleRemoveKeyDown}
              className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/80"
              aria-label={removeAriaLabel}
              tabIndex={0}
            >
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      <FullscreenPinchImageViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        src={resolvedDisplaySrc}
        alt={imageAlt}
        dialogAriaLabel={enlargeAriaLabel}
        closeLabel={closeViewerLabel}
      />
    </>
  );
};
